import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  useTheme,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Icons, toast } from 'react-toastify';
import '../../static/css/InspectionReportList.css'
import { 
  useInspectionReports,
  useInspectionReportActions
} from '../../hooks/useApplications';
import { fieldInspectionService } from '../../services/fieldInspectionService';
import LoadingOverlay from '../Shared/LoadingOverlay';
import ReportDetailsDialog from './ReportDetailsDialog';

const REPORT_STATUS_COLORS = {
  'new': 'info',
  'assigned': 'warning', 
  'in_progress': 'primary',
  'uploaded': 'primary',
  'verified': 'success',
  'rejected': 'error',
};

const REPORT_STATUS_LABELS = {
  'new': 'New Request',
  'assigned': 'Assigned',
  'in_progress': 'In Progress',
  'uploaded': 'Uploaded',
  'verified': 'Verified',
  'rejected': 'Rejected',
};

const REPORT_STATUS_ICONS = {
  'new': <AssignmentIcon />,
  'assigned': <PersonIcon />,
  'in_progress': <RotateRightIcon />,
  'uploaded': <UploadIcon />,
  'verified': <CheckIcon />,
  'rejected': <CancelIcon />,
};

const InspectionReportList = () => {
  const theme = useTheme()
  const { type: assignedListType } = useParams();
  const navigate = useNavigate();
  const { username, role, id: curr_user_id, permissions, isAuthenticated } = useSelector((state) => state.auth);

  // get reportId from query params if present
  const location = useLocation();
  
  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inspectors, setInspectors] = useState([]);
  const [inspectorsLoading, setInspectorsLoading] = useState(false);
  const [inspectorsError, setInspectorsError] = useState(null);
  const [confirmAssignOpen, setConfirmAssignOpen] = useState(false);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const reportIdFromQuery = searchParams.get('reportId');

  const resetInspectors = () => {
    setInspectors([]);
    setSelectedInspector(null);
    setInspectorsLoading(false);
    setInspectorsError(null);
  };

  // Prepare API parameters
  const apiParams = {
    page: page + 1, // API is 1-indexed
    per_page: rowsPerPage,
    search: searchTerm,
  };

  // Get current route to determine which reports to show
  const currentPath = location.pathname;

  // Determine report type based on current route
  const getReportType = () => {
    if (currentPath.includes('new-requests')) {
      return 'new-reports';
    } else if (currentPath.includes('all-assigned-requests')) {
      apiParams.type = assignedListType;
      return 'all-assigned-reports';
    } else if (currentPath.includes('pending-reports')) {
      return 'pending-reports';
    } else if (currentPath.includes('pending-inspections') || currentPath.includes('my-reports')) {
      return 'pending-inspections';
    } else if (currentPath.includes('unverified-reports')) {
      return 'unverified-reports';
    } else if (currentPath.includes('verified-reports')) {
      return 'verified-reports';
    } else {
      return 'new-reports';
    }
  };

  const reportType = getReportType();

  // Use single hook based on report type
  const { data: inspectionData, isLoading, error, refetch } = useInspectionReports(reportType, apiParams);

  // Get action hooks
  const { assign, verify, upload } = useInspectionReportActions();

  const reports = Array.isArray(inspectionData) ? inspectionData : (inspectionData?.reports || []);
  const totalCount = inspectionData?.total || reports.length;

  // Calculate max page and ensure current page is valid
  const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);

  // Get page title based on current route
  const getPageTitle = () => {
    if (currentPath.includes('new-requests')) {
      return 'New Inspection Requests';
    } else if (currentPath.includes('all-assigned-requests')) {
      return 'All Assigned Field Requests';
    } else if (currentPath.includes('pending-reports')) {
      return 'Pending Field Reports';
    } else if (currentPath.includes('pending-inspections')) {
      return 'Pending Inspections';
    } else if (currentPath.includes('my-reports')) {
      return 'My Inspection Reports';
    } else if (currentPath.includes('unverified-reports')) {
      return 'Unverified Reports';
    } else if (currentPath.includes('verified-reports')) {
      return 'Verified Reports';
    } else {
      return 'Field Inspection Reports';
    }
  };

  useEffect(() => {
    if (!reportIdFromQuery || !reports?.length) return;
    
    const reportFromQuery = reports.find(r => r.id.toString() === reportIdFromQuery);

    if (!reportFromQuery) {
      toast.error(`Report with ID ${reportIdFromQuery} not found in the current list`);
      setSearchParams({}, { replace: true });
      return;
    }

    resetInspectors();
    setSelectedReport(reportFromQuery);
    setDialogOpen(true);
    setSearchParams({}, { replace: true });
  }, [reportIdFromQuery, reports, setSearchParams]);

  // Event handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleViewReport = (report) => {
    resetInspectors();
    setSelectedReport(report);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
  };

  const handleEditReport = (reportId) => {
    navigate(`/inspection-report/${reportId}/edit`);
  };

  const handleUploadReport = (reportId) => {
    navigate(`/inspection-report/${reportId}/upload`);
  };

  const handleAssignReport = async (id, report_id, userId) => {
    console.log('Assigning report: id report_id userId | ', id, report_id, userId);
    assign.mutate({ 
      id, 
      assignmentData: { report_id, forward_to_staff_id: userId } 
    });

    setConfirmAssignOpen(false);
    setSelectedInspector(null);
    resetInspectors();
    setDialogOpen(false);
  };
  
  const handleVerifyReport = async (reportId, application_id, action) => {
    verify.mutate({
       reportId, 
       verificationData: { "application_id": application_id, "action": action } 
    });

    setDialogOpen(false);
  };

  const fetchInspectors = async (report_id) => {
    setInspectorsError(null);
    setInspectorsLoading(true);
    try {
      const resp = await fieldInspectionService.getAvailableInspectors(report_id);
      const users = resp?.data || resp || [];
      setInspectors(Array.isArray(users) ? users : (users.items || []));
    } catch (err) {
      setInspectorsError(err.response?.data?.message || err.message || 'Failed to fetch inspectors');
      setInspectors([]);
    } finally {
      setInspectorsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReportStatus = (report) => {
    // if (report.uploaded_at) return 'uploaded';
    // if (report.field_assigned_to) return 'assigned';

    if (report.is_rejected) return 'rejected';
    else if (report.is_verified) return 'verified';
    else if (report.is_report_uploaded) return 'uploaded';
    else if (report.is_assigned) return 'assigned';

    return 'new';
  };

  const canAssignFieldInspection = (report) => {
    const status = getReportStatus(report);
    return  permissions?.includes('process_inspection') &&
            (status !== 'assigned') &&
            report.assigned_to === null;
  }

  const canEditReport = (report) => {
    const status = getReportStatus(report);
    return permissions?.includes('field_inspection') && 
           (status === 'assigned' || status === 'in_progress') &&
           report.assigned_to === curr_user_id;
  };

  const canUploadReport = (report) => {
    const status = getReportStatus(report);
    return permissions?.includes('field_inspection') && 
           status === 'assigned' &&
           report.field_assigned_to === curr_user_id;
  };

  const canVerifyReport = (report) => {
    const status = getReportStatus(report);
    return permissions?.includes('process_inspection') && status === 'uploaded';
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [assignPopperOpen, setAssignPopperOpen] = useState(false);

  const handleToggleAssignPopper = async (event, report) => {
    const currentAnchor = event?.currentTarget || null;
    const willOpen = !assignPopperOpen || anchorEl !== currentAnchor;
    setAssignPopperOpen(willOpen);
    setAnchorEl(willOpen ? currentAnchor : null);
    if (willOpen) {
      // setSelectedReport(report);
      await fetchInspectors(report.id);
    } else {
      resetInspectors();
    }
  };

  const handleCloseAssignPopper = () => {
    setAssignPopperOpen(false);
    setAnchorEl(null);
    resetInspectors();
  };

  const reportActions = (report) => {
    return (
      <Box display="flex" gap={0.5}>                                
        {canEditReport(report) && (
          <Tooltip title="Edit Report">
            <IconButton
              size="medium"
              onClick={() => handleEditReport(report.id)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        {canUploadReport(report) && (
          <Tooltip title="Upload Report">
            <IconButton
              size="medium"
              onClick={() => handleUploadReport(report.id)}
              color="primary"
            >
              <UploadIcon />
            </IconButton>
          </Tooltip>
        )}
        {canVerifyReport(report) && (
          <>
            <Tooltip title="Verify Report">
              <Button
                size="medium"
                onClick={() => handleVerifyReport(report.id, report.application_id, 'verify')}
                variant="contained"
                sx={{ borderColor: 'primary.success', width: 'auto' }}
                color="success"
              >
                <CheckIcon />
                <Typography variant="body2" sx={{ ml: 1 }}>Verify and Forward to SDM</Typography>
              </Button>
            </Tooltip>
            <Tooltip title="Reject Report">
              <Button
                size="medium"
                onClick={() => handleVerifyReport(report.id, report.application_id, 'reject')}
                variant="contained"
                sx={{ borderColor: 'primary.error', width: 'auto' }}
                color="error"
              >
                <CancelIcon />
                <Typography variant="body2" sx={{ ml: 1 }}>Reject and Reassign</Typography>
              </Button>
            </Tooltip>
          </>
        )}
        {canAssignFieldInspection(report) && (
          <>
            <Button
              size="medium"
              onClick={(e) => handleToggleAssignPopper(e, report)}
              variant="outlined"
              sx={{ borderColor: 'primary.main', width: 'auto' }}
            >
              <AssignmentIcon fontSize="medium" />
              <Typography variant="body2" sx={{ ml: 1 }}>Assign Field Inspection Official</Typography>
            </Button>

            <Popper open={assignPopperOpen} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
              <Paper 
                elevation={3} 
                sx={{ 
                  minWidth: anchorEl ? anchorEl.offsetWidth : 320, 
                  maxHeight: 300, 
                  overflow: 'auto' 
                }}
              >
                <ClickAwayListener onClickAway={handleCloseAssignPopper}>
                  <Box p={1}>
                    <Typography variant="subtitle2" gutterBottom>
                      Select Inspector
                    </Typography>
                    {inspectorsLoading ? (
                      <Box display="flex" alignItems="center" gap={1} p={1}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading inspectors...</Typography>
                      </Box>
                    ) : inspectorsError ? (
                      <Box p={1}>
                        <Alert severity="error">{inspectorsError}</Alert>
                      </Box>
                    ) : inspectors.length === 0 ? (
                      <Box p={1}>
                        <Typography variant="body2">No inspectors found for this department</Typography>
                      </Box>
                    ) : (
                      <List dense>
                        {inspectors.map((ins) => (
                          <ListItem disablePadding key={ins.id || ins.username}>
                            <ListItemButton
                              onClick={() => {
                                setSelectedInspector(ins);
                                setConfirmAssignOpen(true);
                                setAssignPopperOpen(false);
                              }}
                            >
                              <ListItemText primary={ins.full_name + ', ' + ins.role.name} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Popper>
          </>
        )}
      </Box>
    )
  }

  // Render loading state
  if (isLoading && reports.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Render no access state
  if (!permissions?.includes('process_inspection') && !permissions?.includes('field_inspection')) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          You don't have permission to view inspection reports.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
                <Typography variant="h5" component="h1">
                  {getPageTitle()}
                </Typography>
                <Box>
                  <IconButton onClick={refetch} disabled={isLoading}>
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Search */}
              <Grid container spacing={2} mb={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    placeholder="Search by application ID, location, or assigned user..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error.response?.data?.message || error.message || 'Failed to fetch inspection reports'}
                </Alert>
              )}

              {/* Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Report ID</TableCell>
                      <TableCell>Application Refno</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Assigned To</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Inspection Date</TableCell>
                      <TableCell>Requested Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.length > 0 ? (
                      reports.map((report) => {
                        const status = getReportStatus(report);
                        return (
                          <TableRow 
                            key={report.id} 
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleViewReport(report)}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                #{report.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                #{report.application_details.application_refno}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <LocationIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {report.location || 'Not specified'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <PersonIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {report.field_assigned_user?.username || 'Unassigned'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={REPORT_STATUS_LABELS[status]}
                                icon={REPORT_STATUS_ICONS[status]}
                                color={REPORT_STATUS_COLORS[status]}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CalendarIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {formatDate(report.inspection_datetime)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(report.requested_at)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="textSecondary">
                            {isLoading ? 'Loading...' : 'No inspection reports found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalCount > 0 && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={Math.min(page, Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1))}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Report Details Dialog */}
      <ReportDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        selectedReport={selectedReport}
        reportActions={reportActions}
        formatDate={formatDate}
        getReportStatus={getReportStatus}
      />
      
      {/* Confirm Assign Dialog */}
      <Dialog
        open={confirmAssignOpen}
        onClose={() => { 
          setConfirmAssignOpen(false); 
          setSelectedInspector(null); 
        }}
      >
        <DialogTitle>Confirm Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Assign Field Inspection #{selectedReport?.id} to <strong>{selectedInspector?.username}</strong>?
          </Typography>
          <Typography variant="body2">
            {/* TODO: Will be showing the current workload of particular inspector
                      <List>
                        {inspectorWorkload.map((item) => (
                          <ListItem key={item.id}>
                            <ListItemText primary={item.report_name} secondary={`Status: ${item.status}`} />
                          </ListItem>
                        ))}
                      </List>
             */}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { 
            setConfirmAssignOpen(false); 
            setSelectedInspector(null); 
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const reportId = selectedReport?.id;
              const userId = selectedInspector?.id;
              const applId = selectedReport?.application_id;

              if (reportId && userId && applId) {
                handleAssignReport(applId, reportId, userId);
              } else {
                toast.error('Missing inspector or report information');
              }
            }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      <LoadingOverlay
        open={isLoading}
        message="Loading inspection reports..."
      />
    </Box>
  );
};

export default InspectionReportList;
